// src/promediaService.js - MQTT Version
import mqtt from 'mqtt';
import webdavService from './webdavService';
import { getRuntimeConfig } from '../config';

class PromediaService {
  constructor() {
    this.client = null;
    this.connectPromise = null;
    this.deviceName = null;
    this.deviceId = null;
    this.isConnected = false;
    this.handlersAttached = false;
    this.pendingCommands = new Map();
    // Increased default timeout to better support longer shell operations (curl downloads, APK installs)
    this.commandTimeout = 90000;
    this.responseCallbacks = new Map();
    this.shellCallbacks = new Map();
    
    const runtime = getRuntimeConfig();
    // MQTT Configuration
    this.mqttConfig = {
      url: runtime.MQTT_WS_URL || '',
      host: '',
      port: 8083,
      protocol: 'wss',
      clientId: `promedia_web_${Math.random().toString(16).substr(2, 8)}`,
      clean: true,
      reconnectPeriod: 3000,
      connectTimeout: 5000,
      keepalive: 30,
      reschedulePings: true
    };
    
    this.topicPrefix = 'SLIDESHOW';
  }

  getDeviceNameFromResponseTopic(topic) {
    if (!topic || typeof topic !== 'string') return null;
    const parts = topic.split('/');
    const respIndex = parts.indexOf('RESP');
    if (respIndex === -1) return null;
    return parts[respIndex + 1] || null;
  }

  getBridgeBaseUrl() {
    const rawCfg = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) ? window.__RUNTIME_CONFIG__ : {};
    const configuredApiBase = (rawCfg.API_BASE_URL || '').trim();
    const runtime = getRuntimeConfig();
    const apiBase = (configuredApiBase || runtime?.API_BASE_URL || '').trim();

    try {
      const hostname = window.location?.hostname || '';
      const protocol = window.location?.protocol || '';
      const isIpHost = /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);
      const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || isIpHost;

      // Internal-only LAN mode (HTTP): keep everything on the same origin.
      // This avoids hairpin/NAT/DNS issues and allows running without public domains.
      if (isLocalHost && protocol === 'http:') {
        return window.location.origin;
      }
    } catch (e) {
      // ignore
    }

    if (apiBase) {
      return apiBase.replace(/\/?api\/?$/, '');
    }
    return window.location.origin;
  }

  async getDeviceInfo(deviceName) {
    if (deviceName) {
      return this.sendCommandToDevice(deviceName, 'deviceInfo');
    }
    return this.sendCommand('deviceInfo');
  }

  async sendCommandWithFallbacks(operation, parameterCandidates = []) {
    let lastError = null;

    for (const parameters of parameterCandidates) {
      try {
        return await this.sendCommand(operation, parameters);
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('Command failed');
  }

  // Initialize MQTT connection
  async connect() {
    if (this.client && this.isConnected) {
      console.log('🔄 MQTT already connected');
      return true;
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    try {
      console.log('🔌 Connecting to MQTT broker...');
      const derivedWsUrl = (() => {
        try {
          if (typeof window === 'undefined' || !window.location) return '';
          const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          return `${proto}//${window.location.host}/ws`;
        } catch {
          return '';
        }
      })();

      const isLocalHost = (() => {
        try {
          const h = window.location.hostname;
          if (!h) return false;
          if (h === 'localhost' || h === '127.0.0.1' || h === '::1') return true;
          if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(h)) return true;
          return false;
        } catch {
          return false;
        }
      })();

      const isHttpPage = (() => {
        try {
          return window.location && window.location.protocol === 'http:';
        } catch {
          return false;
        }
      })();

      const brokerUrl = (isLocalHost && isHttpPage)
        ? (derivedWsUrl || this.mqttConfig.url || `ws://${this.mqttConfig.host}/ws`)
        : (this.mqttConfig.url || derivedWsUrl || `wss://${this.mqttConfig.host}/ws`);
      
      console.log(`🌐 Connecting to: ${brokerUrl}`);
      
      const runtime = getRuntimeConfig();
      const connectOptions = {
        clientId: this.mqttConfig.clientId,
        clean: this.mqttConfig.clean,
        reconnectPeriod: this.mqttConfig.reconnectPeriod,
        connectTimeout: this.mqttConfig.connectTimeout
      };

      if (runtime.MQTT_USERNAME) connectOptions.username = runtime.MQTT_USERNAME;
      if (runtime.MQTT_PASSWORD) connectOptions.password = runtime.MQTT_PASSWORD;

      if (this.client) {
        try {
          this.client.end(true);
        } catch (e) {
          // ignore
        }
        this.client = null;
        this.handlersAttached = false;
      }

      this.client = mqtt.connect(brokerUrl, connectOptions);
      
      this.connectPromise = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('MQTT connection timeout'));
        }, this.mqttConfig.connectTimeout);

        this.client.on('connect', () => {
          clearTimeout(timeout);
          console.log('✅ Connected to MQTT broker');
          this.isConnected = true;
          this.setupEventHandlers();
          resolve(true);
        });

        this.client.on('error', (error) => {
          clearTimeout(timeout);
          console.error('❌ MQTT connection error:', error);
          this.isConnected = false;
          reject(error);
        });
      });
      return await this.connectPromise;
    } catch (error) {
      console.error('❌ Failed to connect to MQTT:', error);
      this.isConnected = false;
      throw error;
    } finally {
      this.connectPromise = null;
    }
  }

  // Setup event handlers for MQTT client
  setupEventHandlers() {
    if (!this.client) return;
    if (this.handlersAttached) return;
    this.handlersAttached = true;

    this.client.on('message', (topic, message) => {
      try {
        const response = JSON.parse(message.toString());
        this.handleResponse(topic, response);
      } catch (error) {
        console.error('❌ Error parsing MQTT message:', error);
      }
    });

    this.client.on('disconnect', () => {
      console.log('📡 MQTT disconnected');
      this.isConnected = false;
    });

    this.client.on('reconnect', () => {
      console.log('🔄 MQTT reconnecting...');
    });

    this.client.on('close', () => {
      console.log('🚪 MQTT connection closed');
      this.isConnected = false;
    });
  }

  // Handle incoming MQTT responses
  handleResponse(topic, response) {
    console.log('📨 MQTT Response received:', { topic, response });

    // Special handling for shell responses on /SHELL topic
    if (topic && topic.endsWith('/SHELL')) {
      const shellDeviceName = this.getDeviceNameFromResponseTopic(topic);
      const shellCommand = response.command;
      if (!shellCommand) {
        console.warn('⚠️ Shell response missing command field:', response);
        return;
      }

      const shellKey = `${shellDeviceName || ''}::${shellCommand}`;
      const queue = this.shellCallbacks.get(shellKey);
      const shellCallback = Array.isArray(queue) ? queue.shift() : null;
      if (shellCallback) {
        if (!queue || queue.length === 0) {
          this.shellCallbacks.delete(shellKey);
        } else {
          this.shellCallbacks.set(shellKey, queue);
        }
        console.log(`✅ Shell response processed for command: ${shellCommand}`);
        shellCallback(response);
      } else {
        console.warn(`⚠️ No shell callback found for command: ${shellCommand}`);
      }
      return;
    }

    const responseCommandId = response.commandId;
    if (responseCommandId) {
      const entry = this.responseCallbacks.get(responseCommandId);
      const callback = typeof entry === 'function' ? entry : entry?.callback;

      // Extra guard: if the pending callback was created for a specific device,
      // only accept a response from that device's RESP topic.
      const responseDeviceName = this.getDeviceNameFromResponseTopic(topic);
      const expectedDeviceName = typeof entry === 'function' ? null : entry?.deviceName;
      if (expectedDeviceName && responseDeviceName && expectedDeviceName !== responseDeviceName) {
        return;
      }

      if (callback) {
        this.responseCallbacks.delete(responseCommandId);
        console.log(`✅ Response processed for commandId: ${responseCommandId}`);
        callback(response);
      } else {
        console.warn(`⚠️ No callback found for commandId: ${responseCommandId}`);
      }
      return;
    }

    const responseCommand = response.command || response.operation;

    if (!responseCommand) {
      console.warn('⚠️ No command field in response:', response);
      return;
    }

    // Find matching callback by command type, scoped by device
    const responseDeviceName = this.getDeviceNameFromResponseTopic(topic);
    let matchedCommandId = null;
    for (const [commandId, entry] of this.responseCallbacks.entries()) {
      const op = typeof entry === 'function' ? null : entry?.operation;
      const dev = typeof entry === 'function' ? null : entry?.deviceName;
      if (op === responseCommand && (!responseDeviceName || dev === responseDeviceName)) {
        matchedCommandId = commandId;
        break;
      }
    }

    if (matchedCommandId) {
      const entry = this.responseCallbacks.get(matchedCommandId);
      const callback = typeof entry === 'function' ? entry : entry?.callback;
      this.responseCallbacks.delete(matchedCommandId);
      console.log(`✅ Response processed for command: ${responseCommand} (${matchedCommandId})`);
      callback?.(response);
    } else {
      // Some devices publish unsolicited/push telemetry (e.g. deviceInfo, playStatistics)
      // that won't have a matching request callback. Ignore to prevent noisy logs and
      // accidental cross-device matching.
      if (responseCommand === 'deviceInfo' || responseCommand === 'playStatistics' || responseCommand === 'httpproxy') {
        return;
      }
      console.warn(`⚠️ No callback found for command: ${responseCommand}`);
    }
  }

  // Set target device
  setDevice(deviceName) {
    this.deviceName = deviceName;
    this.deviceId = deviceName;
    console.log(`🎯 Target device set: ${deviceName}`);
  }

  // Ensure MQTT connection before sending commands
  async ensureConnection() {
    if (!this.isConnected) {
      await this.connect();
    }
  }

  async sendCommandToDevice(deviceName, operation, parameters = {}) {
    await this.ensureConnection();

    if (!deviceName) {
      throw new Error('Device name required.');
    }

    const pendingKey = `${deviceName}::${operation}`;
    if (this.pendingCommands.has(pendingKey)) {
      return this.pendingCommands.get(pendingKey);
    }

    const commandId = `${operation}_${Date.now()}_${Math.random().toString(16).substr(2, 8)}`;
    const command = {
      operation,
      parameters,
      commandId,
      timestamp: new Date().toISOString()
    };

    const targetDeviceId = deviceName;
    const requestTopic = `${this.topicPrefix}/REQ/${targetDeviceId}/API`;
    const responseTopic = `${this.topicPrefix}/RESP/${targetDeviceId}/API`;

    console.log(`📤 Sending MQTT command:`, { requestTopic, command });

    const timeoutMs = operation === 'deviceInfo' ? Math.max(this.commandTimeout, 120000) : this.commandTimeout;

    const promise = new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const timeout = setTimeout(() => {
        this.responseCallbacks.delete(commandId);
        console.log(`⏰ Command timeout for ${operation} after ${timeoutMs}ms`);
        reject(new Error(`Command timeout - device may be offline or slow to respond`));
      }, timeoutMs);

      this.responseCallbacks.set(commandId, {
        callback: (response) => {
        clearTimeout(timeout);
        const durationMs = Date.now() - startedAt;
        console.log(`⏱️ ${operation} (${commandId}) completed in ${durationMs}ms`);
        console.log(`📨 Received response for ${operation}:`, response);

        if (response.success !== false) {
          resolve(response);
        } else {
          reject(new Error(response.error || response.errorMessage || 'Command failed'));
        }
        },
        operation,
        deviceName
      });

      if (!this.subscribedTopics?.has(responseTopic)) {
        this.client.subscribe(responseTopic, (err) => {
          if (err) {
            clearTimeout(timeout);
            this.responseCallbacks.delete(commandId);
            reject(new Error(`Failed to subscribe to response topic: ${err.message}`));
            return;
          }

          if (!this.subscribedTopics) this.subscribedTopics = new Set();
          this.subscribedTopics.add(responseTopic);
          console.log(`📡 Subscribed to: ${responseTopic}`);

          this.client.publish(requestTopic, JSON.stringify(command), (err) => {
            if (err) {
              clearTimeout(timeout);
              this.responseCallbacks.delete(commandId);
              reject(new Error(`Failed to publish command: ${err.message}`));
            } else {
              console.log(`✅ Command published: ${operation}`);
            }
          });
        });
      } else {
        this.client.publish(requestTopic, JSON.stringify(command), (err) => {
          if (err) {
            clearTimeout(timeout);
            this.responseCallbacks.delete(commandId);
            reject(new Error(`Failed to publish command: ${err.message}`));
          } else {
            console.log(`✅ Command published: ${operation}`);
          }
        });
      }
    });

    this.pendingCommands.set(pendingKey, promise);
    promise.finally(() => {
      this.pendingCommands.delete(pendingKey);
    });
    return promise;
  }

  // Send MQTT command and wait for response
  async sendCommand(operation, parameters = {}) {
    await this.ensureConnection();

    if (!this.deviceName || !this.deviceId) {
      throw new Error('Device not set. Call setDevice() first.');
    }

    const commandId = `${operation}_${Date.now()}_${Math.random().toString(16).substr(2, 8)}`;
    const command = {
      operation,
      parameters,
      commandId,
      timestamp: new Date().toISOString()
    };

    const requestTopic = `${this.topicPrefix}/REQ/${this.deviceId}/API`;
    const responseTopic = `${this.topicPrefix}/RESP/${this.deviceId}/API`;

    console.log(`📤 Sending MQTT command:`, { requestTopic, command });

    return new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const timeout = setTimeout(() => {
        this.responseCallbacks.delete(commandId);
        console.log(`⏰ Command timeout for ${operation} after ${this.commandTimeout}ms`);
        reject(new Error(`Command timeout - device may be offline or slow to respond`));
      }, this.commandTimeout);

      this.responseCallbacks.set(commandId, {
        callback: (response) => {
          clearTimeout(timeout);
          const durationMs = Date.now() - startedAt;
          console.log(`⏱️ ${operation} (${commandId}) completed in ${durationMs}ms`);
          console.log(`📨 Received response for ${operation}:`, response);
          
          if (response.success !== false) {
            resolve(response);
          } else {
            reject(new Error(response.error || response.errorMessage || 'Command failed'));
          }
        },
        operation,
        deviceName: this.deviceId
      });

      if (!this.subscribedTopics?.has(responseTopic)) {
        this.client.subscribe(responseTopic, (err) => {
          if (err) {
            clearTimeout(timeout);
            this.responseCallbacks.delete(commandId);
            reject(new Error(`Failed to subscribe to response topic: ${err.message}`));
            return;
          }
          
          if (!this.subscribedTopics) this.subscribedTopics = new Set();
          this.subscribedTopics.add(responseTopic);
          console.log(`📡 Subscribed to: ${responseTopic}`);
          
          this.client.publish(requestTopic, JSON.stringify(command), (err) => {
            if (err) {
              clearTimeout(timeout);
              this.responseCallbacks.delete(commandId);
              reject(new Error(`Failed to publish command: ${err.message}`));
            } else {
              console.log(`✅ Command published: ${operation}`);
            }
          });
        });
      } else {
        this.client.publish(requestTopic, JSON.stringify(command), (err) => {
          if (err) {
            clearTimeout(timeout);
            this.responseCallbacks.delete(commandId);
            reject(new Error(`Failed to publish command: ${err.message}`));
          } else {
            console.log(`✅ Command published: ${operation}`);
          }
        });
      }
    });
  }

  // === DEVICE ACCESS METHODS ===
  
  /**
   * Open device web interface via FRP tunnel
   * Opens in new tab: https://<devicename>.<domain>
   */
  async openDeviceInterface() {
    throw new Error('FRP is removed in this new_srv build');
  }

// === Open device control via MQTT Bridge ===
async openMqttBridge(deviceName) {
    const targetDevice = deviceName || this.deviceName;
    if (!targetDevice) {
      throw new Error('Device not set. Pass deviceName or call setDevice() first.');
    }

    console.log(`🎛️ Opening MQTT Bridge control in modal: ${targetDevice}`);
    const baseUrl = this.getBridgeBaseUrl();
    
    try {
      // Get auth token
      const authToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      if (authToken) {
        try {
          await fetch(`${baseUrl}/device/${encodeURIComponent(targetDevice)}/check-auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ token: authToken })
          });
        } catch (e) {
          // Non-fatal: still open the bridge UI.
          // In LAN-only deployments / when check-auth is not enabled, the iframe may still work.
          console.warn('⚠️ Bridge check-auth failed, continuing to open UI:', e);
        }
      }
      
      // Create fullscreen iframe overlay (no address bar visible)
      const overlay = document.createElement('div');
      overlay.id = 'mqtt-bridge-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.95);
        z-index: 999999;
        display: flex;
        flex-direction: column;
      `;

      // Header with close button
      const header = document.createElement('div');
      header.style.cssText = `
        background: #2196F3;
        color: white;
        padding: 12px 20px;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      `;

      const closeButton = document.createElement('button');
      closeButton.textContent = '✕ Close';
      closeButton.style.cssText = `
        background: #f44336;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: bold;
        transition: background 0.2s;
      `;
      closeButton.onmouseover = () => closeButton.style.background = '#d32f2f';
      closeButton.onmouseout = () => closeButton.style.background = '#f44336';
      closeButton.onclick = () => {
        document.body.removeChild(overlay);
      };
      header.appendChild(closeButton);

      // Create iframe
      const iframe = document.createElement('iframe');
      const bridgeUrl = `${baseUrl}/device/${encodeURIComponent(targetDevice)}`;
      iframe.src = bridgeUrl;
      iframe.style.cssText = `
        flex: 1;
        border: none;
        width: 100%;
        background: white;
      `;

      // Loading indicator
      const loading = document.createElement('div');
      loading.textContent = '⏳ Loading device interface...';
      loading.style.cssText = `
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: black;
        font-size: 18px;
        font-weight: 500;
      `;

      iframe.onload = () => {
        if (loading.parentNode) {
          loading.remove();
        }
      };

      // Close on ESC key
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          document.body.removeChild(overlay);
          document.removeEventListener('keydown', handleEscape);
        }
      };
      document.addEventListener('keydown', handleEscape);

      overlay.appendChild(header);
      overlay.appendChild(loading);
      overlay.appendChild(iframe);
      document.body.appendChild(overlay);

      console.log(`✅ MQTT Bridge opened in secure modal`);
      
      return { 
        success: true, 
        message: 'MQTT Bridge opened in secure modal (no URL visible)'
      };
    } catch (error) {
      console.error('❌ Failed to open MQTT Bridge:', error);
      throw error;
    }
  }

  // ===== QUICK-MANAGE BRIDGE HELPERS =====
  // Used by DeviceQuickManageModal: targeted GET/POST against the agent's HTTP layer
  // through the MQTT-HTTP bridge. Keeps users out of the full device dashboard.

  async ensureBridgeAuth(/* deviceName */) {
    // No-op now: the bridge has no auth layer and forwards everything to the
    // device. The device authenticates via Basic admin:admin which the bridge
    // injects automatically when no Authorization header is present.
    // Kept as a placeholder so callers don't need to change.
    return;
  }

  async fetchDeviceHtml(deviceName, path) {
    const baseUrl = this.getBridgeBaseUrl();
    const url = `${baseUrl}/device/${encodeURIComponent(deviceName)}${path}`;
    // credentials:'omit' — the bridge has no auth layer; forwarding the browser
    // session cookie was confusing the device's HTTP auth and triggering a 401
    // (the agent sees a stale cookie and refuses Basic auth fallback).
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`Bridge ${res.status} on ${path}`);
    return await res.text();
  }

  // 2026-05-20 — list the files the agent actually has on its local
  // storage (NOT the WebDAV sync source). Calls the agent's elFinder
  // `open` API; for the root, target is omitted so the agent returns
  // cwd + files for the default media volume. Response shape (elFinder):
  //   { cwd: {...}, files: [ {hash, name, mime, size, ...}, ... ], ... }
  // We filter out directories and map to { basename } so the existing
  // RemoteControlModal file-picker can consume the result unchanged.
  //
  // Endpoint: ResponderImpl line 803 maps anything starting with
  // `/api/file/browser` to the ApiResponder. The cmd param drives which
  // sub-command runs (open, ls, play, get, ...).
  async listDeviceLocalFiles(deviceName) {
    const baseUrl = this.getBridgeBaseUrl();
    const url = `${baseUrl}/device/${encodeURIComponent(deviceName)}/api/file/browser?cmd=open&target=&init=1`;
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`Bridge ${res.status} on /api/file/browser`);
    const data = await res.json();
    const files = Array.isArray(data?.files) ? data.files : [];
    // Filter to actual files (not dirs / not the cwd entry itself). elFinder
    // marks directories with `mime: 'directory'`. The cwd entry has the same
    // hash as `cwd.hash` and we don't want it in the list.
    const cwdHash = data?.cwd?.hash;
    return files
      .filter(f => f && f.name && f.mime !== 'directory' && f.hash !== cwdHash)
      .map(f => ({
        basename: f.name,
        size:     f.size || 0,
        modified: f.ts ? new Date(f.ts * 1000).toISOString() : '',
        mime:     f.mime || '',
      }));
  }

  async postDeviceForm(deviceName, path, formData) {
    const baseUrl = this.getBridgeBaseUrl();
    const url = `${baseUrl}/device/${encodeURIComponent(deviceName)}${path}`;
    const body = new URLSearchParams(formData).toString();
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body
    });
    if (!res.ok) throw new Error(`Bridge ${res.status} on POST ${path}`);
    return await res.text();
  }

  _parseHtmlDoc(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  _idFromHref(href, paramName) {
    if (!href) return null;
    const m = href.match(new RegExp(`[?&]${paramName}=([^&#]+)`));
    return m ? decodeURIComponent(m[1]) : null;
  }

  // ---------- Layouts ----------
  async listLayouts(deviceName) {
    const html = await this.fetchDeviceHtml(deviceName, '/screen_layout');
    const doc = this._parseHtmlDoc(html);
    const rows = doc.querySelectorAll('table.styledTable tbody tr, table.styledTable tr');
    const layouts = [];
    rows.forEach(tr => {
      const tds = tr.querySelectorAll('td');
      if (tds.length < 2) return;
      const name = (tds[0].textContent || '').trim();
      if (!name) return;
      const editLink = tr.querySelector('a[href*="/screen_layout/edit?layout="]');
      const activateLink = tr.querySelector('a[href*="/screen_layout?activate="]');
      const id = this._idFromHref(editLink?.getAttribute('href'), 'layout')
              || this._idFromHref(activateLink?.getAttribute('href'), 'activate');
      if (!id) return;
      const isActive = (tds[1].textContent || '').toLowerCase().includes('active');
      layouts.push({ id, name, isActive });
    });
    return layouts;
  }

  async activateLayoutById(deviceName, layoutId) {
    return this.fetchDeviceHtml(deviceName, `/screen_layout?activate=${encodeURIComponent(layoutId)}`);
  }

  // ---------- Playlists ----------
  async listPlaylists(deviceName) {
    const html = await this.fetchDeviceHtml(deviceName, '/playlists');
    const doc = this._parseHtmlDoc(html);
    const rows = doc.querySelectorAll('table.styledTable tbody tr, table.styledTable tr');
    const playlists = [];
    rows.forEach(tr => {
      const tds = tr.querySelectorAll('td');
      if (tds.length < 4) return;
      const name = (tds[0].textContent || '').trim();
      if (!name) return;
      const editLink = tr.querySelector('a[href*="/playlists/edit?playlist="]');
      const id = this._idFromHref(editLink?.getAttribute('href'), 'playlist');
      if (!id) return;
      const number = (tds[1].textContent || '').trim();
      const sound = (tds[2].textContent || '').trim();
      const statusCell = (tds[3].textContent || '').trim();
      const isActive = /^active$/i.test(statusCell);
      playlists.push({ id, name, number, sound, isActive });
    });
    return playlists;
  }

  async activatePlaylistById(deviceName, playlistId) {
    return this.fetchDeviceHtml(deviceName, `/playlists?activate=${encodeURIComponent(playlistId)}`);
  }

  // ---------- Playlist edit form (slide-time + meta) ----------
  async getPlaylistEdit(deviceName, playlistId) {
    const html = await this.fetchDeviceHtml(deviceName, `/playlists/edit?playlist=${encodeURIComponent(playlistId)}`);
    const doc = this._parseHtmlDoc(html);
    const form = doc.querySelector('form[method="post"]');
    if (!form) throw new Error('Playlist form not found in response');

    const data = {};
    form.querySelectorAll('input[name]').forEach(input => {
      if (input.type === 'checkbox' && !input.checked) return;
      if (input.type === 'submit') return;
      data[input.name] = input.value ?? '';
    });
    form.querySelectorAll('select[name]').forEach(sel => {
      data[sel.name] = sel.value ?? '';
    });
    form.querySelectorAll('textarea[name]').forEach(t => {
      data[t.name] = t.value ?? '';
    });
    return data;
  }

  async savePlaylistEdit(deviceName, playlistId, formData) {
    // Save and verify: POST the form, then re-GET and confirm `duration` persisted.
    // The agent may return 200 even when it silently rejected the form (CSRF,
    // missing fields). Confirm by reading back.
    await this.postDeviceForm(deviceName, `/playlists/edit?playlist=${encodeURIComponent(playlistId)}`, formData);
    const after = await this.getPlaylistEdit(deviceName, playlistId);
    const want = String(formData.duration ?? '').trim();
    const got  = String(after.duration ?? '').trim();
    if (want && want !== got) {
      throw new Error(`Save did not persist (wanted duration=${want}, device shows ${got}).`);
    }
    return after;
  }

  // ---------- Layout edit (rotation, name, per-panel transitions) ----------
  async getLayoutEdit(deviceName, layoutId) {
    const html = await this.fetchDeviceHtml(deviceName, `/screen_layout/edit?layout=${encodeURIComponent(layoutId)}`);
    const doc = this._parseHtmlDoc(html);
    const form = doc.querySelector('form#builderForm') || doc.querySelector('form[action*="/screen_layout/edit"]');
    const fields = {};
    if (form) {
      form.querySelectorAll('input[name]').forEach(input => {
        if (input.type === 'submit') return;
        fields[input.name] = input.value ?? '';
      });
    }
    // Pull the panels JSON from inline script:  var panels = [...]
    let panels = [];
    const m = html.match(/var\s+panels\s*=\s*(\[[\s\S]*?\])\s*;/);
    if (m) {
      try { panels = JSON.parse(m[1]); } catch (e) { console.warn('panels JSON parse failed', e); }
    }
    return { fields, panels };
  }

  async saveLayoutEdit(deviceName, layoutId, fields, panels, { activate = true } = {}) {
    // The agent does NOT accept a `panels` JSON field. Its layoutBuilder.js
    // serializes panels as a flat `list[i].field=value` and
    // `list[i].properties["KEY"]=value`. Match that exact wire format or the
    // POST silently no-ops (returns 200 but doesn't persist).
    const fd = { ...fields, layout: String(layoutId) };
    panels.forEach((p, i) => {
      const prefix = `list[${i}]`;
      fd[`${prefix}.id`] = p.id ?? '';
      fd[`${prefix}.name`] = p.name ?? '';
      fd[`${prefix}.width`] = p.width ?? '';
      fd[`${prefix}.height`] = p.height ?? '';
      fd[`${prefix}.left`] = p.left ?? '';
      fd[`${prefix}.top`] = p.top ?? '';
      fd[`${prefix}.displayOrder`] = i;
      fd[`${prefix}.main`] = p.main ? 'true' : 'false';
      fd[`${prefix}.backgroundColor`] = p.backgroundColor ?? '';
      fd[`${prefix}.displayId`] = p.displayId ?? '';
      const props = p.properties || {};
      Object.entries(props).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        fd[`${prefix}.properties["${k}"]`] = v;
      });
    });
    await this.postDeviceForm(deviceName, '/screen_layout/edit', fd);

    // Verify the change persisted (read back).
    const after = await this.getLayoutEdit(deviceName, layoutId);
    panels.forEach((p, i) => {
      const want = p.properties?.ANIMATION_TYPE;
      const got  = after.panels?.[i]?.properties?.ANIMATION_TYPE;
      if (want && got && want !== got) {
        throw new Error(`Save did not persist for zone "${p.name}" (wanted ${want}, device shows ${got}).`);
      }
    });

    // If the saved layout is the active one, re-activate it so the device picks
    // up the new transitions immediately (otherwise changes only apply on next
    // scheduled switch).
    if (activate) {
      try { await this.activateLayoutById(deviceName, layoutId); } catch (e) { /* non-fatal */ }
    }
    return after;
  }

  // ---------- Settings: read/write Synchronization interval (grabber_refresh_rate) ----------
  // The agent's settings POST handler accepts partial form data: fields not
  // present in the POST body are left unchanged. Verified live by setting
  // grabber_refresh_rate alone and confirming other settings stayed put.
  async getSettingsValue(deviceName, fieldName) {
    const html = await this.fetchDeviceHtml(deviceName, '/settings');
    // Match either pattern: <input name='X' ... value='Y'> or value="Y"
    const re = new RegExp(`name=['"]${fieldName}['"][^>]*?value=['"]([^'"]*)['"]`, 'i');
    const m = html.match(re);
    return m ? m[1] : '';
  }
  async setSettingsValue(deviceName, fieldName, value) {
    const fd = { [fieldName]: String(value ?? '') };
    return this.postDeviceForm(deviceName, '/settings', fd);
  }
  // ── License (agent page /about_software) — 2026-11-04 ──────────────────
  // The agent renders:
  //   ...device ID: <b>DAAAB74CAB3</b> ...
  //   <form method='post' action='/aboutsoftware'>
  //     <input type='text' name='license_key' value='...'>
  //   <h3>USB Auto-Import</h3>...<b>Status: </b><span ...>✓ Permission Granted</span>
  // Every field is optional: an older agent build may not render all of them,
  // and a missing piece must leave the tab usable rather than throw.
  async getLicenseInfo(deviceName) {
    const html = await this.fetchDeviceHtml(deviceName, '/about_software');

    const idM  = html.match(/device\s*ID:\s*<b>\s*([^<]+?)\s*<\/b>/i);
    const keyM = html.match(/name=['"]license_key['"][^>]*?value=['"]([^'"]*)['"]/i);

    // The USB block: take the first <span> after the "Status:" label inside it.
    let usbStatus = '';
    const usbIdx = html.search(/USB\s*Auto-?Import/i);
    if (usbIdx !== -1) {
      const chunk = html.slice(usbIdx, usbIdx + 600);
      const stM = chunk.match(/<b>\s*Status:\s*<\/b>\s*<span[^>]*>([\s\S]*?)<\/span>/i);
      if (stM) usbStatus = stM[1].replace(/<[^>]*>/g, '').trim();
    }

    return {
      deviceId:   idM  ? idM[1].trim()  : '',
      licenseKey: keyM ? keyM[1]        : '',
      usbStatus,
      // "Permission Not Granted" also contains "granted" — exclude the
      // negative form explicitly or the warning renders as a success.
      usbGranted: /granted/i.test(usbStatus) && !/\bnot\b/i.test(usbStatus),
      hasForm:    !!keyM,
    };
  }

  // Posts the key exactly the way the agent's own form does. Note the action
  // is '/aboutsoftware' (no underscore) while the page is '/about_software'.
  //
  // The agent DOES judge the key and says so in the page it returns:
  //   <div class='okBubble'>Invalid license key! Please check and try again.</div>
  // (verified against a real player). Earlier this method threw the response
  // away and the caller reported success for a key the player had rejected.
  async saveLicenseKey(deviceName, key) {
    const html = await this.postDeviceForm(deviceName, '/aboutsoftware', {
      license_key: String(key ?? '').trim(),
    });

    const m = String(html || '').match(/<div[^>]*class=['"][^'"]*okBubble[^'"]*['"][^>]*>([\s\S]*?)<\/div>/i);
    const message = m ? m[1].replace(/<[^>]*>/g, '').trim() : '';
    const rejected = /invalid|incorrect|wrong|error|fail|not\s+valid|expired/i.test(message);

    return {
      message,                               // the agent's own words, shown verbatim
      rejected,
      confirmed: !!message && !rejected,     // the agent explicitly said something good
    };
  }

  async getGrabberInterval(deviceName) {
    const v = await this.getSettingsValue(deviceName, 'grabber_refresh_rate');
    return v || '0';
  }
  async setGrabberInterval(deviceName, seconds) {
    return this.setSettingsValue(deviceName, 'grabber_refresh_rate', seconds);
  }

  // ---------- File synchronization (grabber / WebDAV) ----------
  async listGrabbers(deviceName) {
    const html = await this.fetchDeviceHtml(deviceName, '/grabber');
    const doc = this._parseHtmlDoc(html);
    const list = [];
    // Scope to the FIRST styledTable only — the second one is the run-history.
    const table = doc.querySelector('table.styledTable');
    if (!table) return list;
    const rows = table.querySelectorAll('tbody tr, tr');
    rows.forEach(tr => {
      if (tr.querySelector('th')) return; // skip header row
      const tds = tr.querySelectorAll('td');
      if (tds.length < 4) return;
      const from = (tds[0].textContent || '').trim();
      const to   = (tds[1].textContent || '').trim();
      const clear = (tds[2].textContent || '').trim();
      if (!from) return;
      // ID comes from the Sync/Edit/Delete controls in the 4th cell.
      const dl   = tr.querySelector('input[name="download"]');
      const ed   = tr.querySelector('a[href*="/grabber?update="]');
      const del  = tr.querySelector('input[name="delete"]');
      const id   = (dl?.value) || this._idFromHref(ed?.getAttribute('href'), 'update') || (del?.value) || '';
      list.push({ id, from, to, clear });
    });
    return list;
  }

  // Read the run-history table (second styledTable on /grabber). Used by the UI
  // to detect when a manually-triggered sync has completed.
  async listGrabberHistory(deviceName) {
    const html = await this.fetchDeviceHtml(deviceName, '/grabber');
    const doc = this._parseHtmlDoc(html);
    const tables = doc.querySelectorAll('table.styledTable');
    if (tables.length < 2) return [];
    const out = [];
    tables[1].querySelectorAll('tr').forEach(tr => {
      if (tr.querySelector('th')) return;
      const tds = tr.querySelectorAll('td');
      if (tds.length < 5) return;
      out.push({
        from:     (tds[0].textContent || '').trim(),
        to:       (tds[1].textContent || '').trim(),
        started:  (tds[2].textContent || '').trim(),
        finished: (tds[3].textContent || '').trim(),
        result:   (tds[4].textContent || '').trim()
      });
    });
    return out;
  }

  // Load the WebDAV sync config for a specific sync id (or fall back to the
  // first existing sync). The agent's edit-mode page (`?update=<id>`) renders
  // the WebDAV form pre-filled if that sync is a WebDAV type.
  async getGrabberWebdav(deviceName, idArg) {
    let id = idArg;
    if (!id) {
      const list = await this.listGrabbers(deviceName);
      if (list.length === 0) return null;
      id = list[0].id;
    }
    if (!id) return null;
    const html = await this.fetchDeviceHtml(deviceName, `/grabber?update=${encodeURIComponent(id)}`);
    const doc = this._parseHtmlDoc(html);
    const forms = doc.querySelectorAll('form[action*="/grabber"]');
    let webdavForm = null;
    forms.forEach(f => {
      if (f.querySelector('input[name="webdav"][value="true"]')) webdavForm = f;
    });
    if (!webdavForm) return null;
    const get = (name) => {
      const el = webdavForm.querySelector(`[name="${name}"]`);
      return el ? (el.value || '') : '';
    };
    // fileName is a <select> in the webdav form — its value is the selected option.
    let folder = '';
    const folderSel = webdavForm.querySelector('select[name="fileName"]');
    if (folderSel) {
      const opt = folderSel.querySelector('option[selected]') || folderSel.querySelector('option');
      folder = opt ? opt.getAttribute('value') || opt.textContent.trim() : '';
    } else {
      folder = get('fileName');
    }
    const clearEl = webdavForm.querySelector('[name="clearFolder4"]');
    return {
      id: String(id),
      url: get('url'),
      username: get('username1'),
      password: get('password1'),
      folder: folder || '/',
      clearFolder: !!(clearEl && (clearEl.value || '').toLowerCase() === 'true')
    };
  }

  // Save (overwrite) when `id` is provided; create new sync when `id` is empty.
  async saveGrabberWebdav(deviceName, { id, url, username, password, folder, clearFolder }) {
    // Field names verified against the agent's GrabberFormPage.smali:
    //   • `webdav=true` selects the WebDAV branch (processUrl)
    //   • `url` / `username1` / `password1` consumed by processWebDav
    //   • `fileName` = LOCAL folder on the device (processFileName)
    //   • The agent OR's clearFolder + clearFolder2..5 together, so sending
    //     the value on every variant is safe and survives agent template churn.
    const cf = clearFolder ? 'true' : 'false';
    const fd = {
      webdav: 'true',
      url,
      username1: username || '',
      password1: password || '',
      fileName: folder || '/',
      clearFolder:  cf,
      clearFolder2: cf,
      clearFolder3: cf,
      clearFolder4: cf,
      clearFolder5: cf,
    };
    if (id) fd.id = String(id); // presence of `id` flips agent into update mode
    console.log('[saveGrabberWebdav] POST /grabber fields:', { ...fd, password1: fd.password1 ? '••••' : '' });
    const html = await this.postDeviceForm(deviceName, '/grabber', fd);
    // The agent ALWAYS returns 200 OK with the re-rendered form. Success is
    // signalled by a single `<div class="okBubble">…</div>`; validation
    // failures put `<div class="errorBubble">…</div>` plus per-field
    // `<span class='error'>…</span>` markers in the same body. Without
    // parsing this, a silent reject (e.g. fileName not in $allFolders,
    // url_invalid, webdav creds blank) looks identical to a success.
    const doc = this._parseHtmlDoc(html);
    const errorBubble = doc.querySelector('.errorBubble');
    const okBubble    = doc.querySelector('.okBubble');
    console.log('[saveGrabberWebdav] result:', {
      okBubble:    okBubble    ? (okBubble.textContent    || '').trim() : null,
      errorBubble: errorBubble ? (errorBubble.textContent || '').trim() : null,
      fieldErrors: Array.from(doc.querySelectorAll('span.error')).map(s => (s.textContent || '').trim()).filter(Boolean),
      htmlLength:  html.length,
    });
    if (errorBubble) {
      const fieldErrs = Array.from(doc.querySelectorAll('span.error'))
        .map(s => (s.textContent || '').trim()).filter(Boolean);
      const root = (errorBubble.textContent || '').trim();
      const msg = [root, ...fieldErrs].filter(Boolean).join(' — ') || 'Device rejected the save';
      throw new Error(msg);
    }
    if (!okBubble) {
      // No success bubble either — the agent didn't even run the save path.
      // Surface a snippet of the response so we can diagnose from console.
      console.warn('[saveGrabberWebdav] no okBubble — first 800 chars of response:\n', html.slice(0, 800));
      throw new Error('Device did not confirm save (no success message in response)');
    }
    return html;
  }

  async grabberSyncNow(deviceName, id) {
    // The device requires the sync's own id; with multiple syncs configured the
    // form button submits download=<id>. Hardcoding 1 silently no-ops on devices
    // where the first sync's id is something else (e.g. 2 or 3).
    return this.postDeviceForm(deviceName, '/grabber', { download: String(id) });
  }

  async grabberDelete(deviceName, id) {
    return this.postDeviceForm(deviceName, '/grabber', { delete: String(id) });
  }

  // ---------- RSS messages ----------
  async listRssMessages(deviceName) {
    const html = await this.fetchDeviceHtml(deviceName, '/rss');
    const doc = this._parseHtmlDoc(html);
    const messages = [];
    const tables = doc.querySelectorAll('table.styledTable');
    tables.forEach(t => {
      const rows = t.querySelectorAll('tr');
      rows.forEach(tr => {
        const tds = tr.querySelectorAll('td');
        if (tds.length < 2) return;
        if (tr.querySelector('th')) return; // header row
        const title = (tds[0].textContent || '').trim();
        const content = (tds[1].textContent || '').trim();
        if (!title && !content) return;
        // Pull the id from the Update or Delete link: /rss?update=ID  /rss?delete=ID
        const a = tr.querySelector('a[href*="/rss?update="], a[href*="/rss?delete="]');
        const id = a ? (this._idFromHref(a.getAttribute('href'), 'update') || this._idFromHref(a.getAttribute('href'), 'delete')) : '';
        messages.push({ id: id || '', title, content });
      });
    });
    return messages;
  }

  // Load the prefilled edit form for one RSS message (?update=ID).
  async getRssMessage(deviceName, id) {
    const html = await this.fetchDeviceHtml(deviceName, `/rss?update=${encodeURIComponent(id)}`);
    const doc = this._parseHtmlDoc(html);
    const form = doc.querySelector('form[action*="/rss"]');
    if (!form) throw new Error('RSS edit form not found');
    const get = (name) => {
      const el = form.querySelector(`[name="${name}"]`);
      return el ? (el.value || '') : '';
    };
    return { id: get('id') || String(id), title: get('title'), content: get('content') };
  }

  async addRssMessage(deviceName, title, content) {
    return this.postDeviceForm(deviceName, '/rss', { title, content });
  }

  async updateRssMessage(deviceName, id, title, content) {
    // Same endpoint, presence of `id` switches to update mode.
    return this.postDeviceForm(deviceName, '/rss', { id: String(id), title, content });
  }

  async deleteRssMessage(deviceName, id) {
    // The agent deletes via GET /rss?delete=ID (matches the link in the listing).
    return this.fetchDeviceHtml(deviceName, `/rss?delete=${encodeURIComponent(id)}`);
  }

  // ---------- Schedules ----------
  async getScreenLayoutSchedule(deviceName) {
    const html = await this.fetchDeviceHtml(deviceName, '/screen_layout/schedule');
    return this._parseScheduleForm(html, 'screenLayoutId');
  }

  async getPanelSchedule(deviceName, panelId) {
    const html = await this.fetchDeviceHtml(deviceName, `/screen_layout/panel/schedule?panelId=${encodeURIComponent(panelId)}`);
    return this._parseScheduleForm(html, 'playlistId');
  }

  async saveScreenLayoutSchedule(deviceName, rows) {
    const fd = this._encodeScheduleRows(rows, 'screenLayoutId');
    return this.postDeviceForm(deviceName, '/screen_layout/schedule', fd);
  }

  async savePanelSchedule(deviceName, panelId, rows) {
    const fd = this._encodeScheduleRows(rows, 'playlistId');
    return this.postDeviceForm(deviceName, `/screen_layout/panel/schedule?panelId=${encodeURIComponent(panelId)}`, fd);
  }

  _parseScheduleForm(html, refField) {
    const doc = this._parseHtmlDoc(html);
    const form = doc.querySelector('form[method="post"]');
    if (!form) return { rows: [], options: [] };
    // Options for the reference select (layouts or playlists). Take the first matching select.
    const sample = form.querySelector(`select[name^="${refField}"]`);
    const options = [];
    if (sample) {
      sample.querySelectorAll('option').forEach(o => options.push({ id: o.value, name: o.textContent.trim() }));
    }
    // Each row indexed by N — collect all "<refField>N" then read the rest by same N.
    const rows = [];
    form.querySelectorAll(`select[name^="${refField}"]`).forEach(sel => {
      const idx = sel.name.replace(refField, '');
      const get = (prefix) => {
        const el = form.querySelector(`[name="${prefix}${idx}"]`);
        return el ? (el.value || '') : '';
      };
      const checked = (prefix) => {
        const el = form.querySelector(`[name="${prefix}${idx}"]`);
        return !!(el && el.value && el.value.toLowerCase() === 'true');
      };
      rows.push({
        id: get('id'),
        ref: sel.value,
        timeFrom: get('timeFrom') || '00:00',
        timeTo: get('timeTo') || '23:59',
        dateFrom: get('dateFrom'),
        dateTo: get('dateTo'),
        priority: get('priority') || '3',
        monday: checked('monday'),
        tuesday: checked('tuesday'),
        wednesday: checked('wednesday'),
        thursday: checked('thursday'),
        friday: checked('friday'),
        saturday: checked('saturday'),
        sunday: checked('sunday')
      });
    });
    return { rows, options };
  }

  _encodeScheduleRows(rows, refField) {
    const fd = {};
    rows.forEach((r, i) => {
      fd[`id${i}`] = r.id || '';
      fd[`${refField}${i}`] = r.ref;
      fd[`timeFrom${i}`] = r.timeFrom;
      fd[`timeTo${i}`] = r.timeTo;
      fd[`dateFrom${i}`] = r.dateFrom || '';
      fd[`dateTo${i}`] = r.dateTo || '';
      fd[`priority${i}`] = r.priority || '3';
      ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].forEach(d => {
        if (r[d]) fd[`${d}${i}`] = 'true';
      });
    });
    return fd;
  }

  // ---------- Panels (used by panel-schedule tab) ----------
  async listPanels(deviceName) {
    try {
      const text = await this.fetchDeviceHtml(deviceName, '/ajax/panels');
      const idx = text.indexOf('{');
      const jsonStr = idx >= 0 ? text.slice(idx) : text;
      const obj = JSON.parse(jsonStr);
      return Object.entries(obj).map(([id, name]) => ({ id, name }));
    } catch (e) {
      console.warn('listPanels failed:', e.message);
      return [];
    }
  }

  async getZones() {
    return this.sendCommand('zones');
  }

  // === VOLUME CONTROL ===
  async getVolume() {
    return this.sendCommand('volume/get');
  }

  async setVolume(volumePercentage) {
    try {
      const res = await this.sendCommand('volume/set', { volPercentage: volumePercentage });
      if (res && res.success !== false) {
        return res;
      }
    } catch (e) {
      // fall through
    }

    return this.sendCommand('volume/set', { vol: volumePercentage });
  }

  // === PLAYBACK CONTROL - FIXED ===
  async playNext(zoneId = null) {
    const params = {};
    // FIXED: Only add zoneName for actual zone names, not numeric IDs
    if (zoneId && zoneId !== 'Whole screen' && isNaN(zoneId)) {
      params.zoneName = zoneId;
    }
    return this.sendCommand('next', params);
  }

  async playPrevious(zoneId = null) {
    const params = {};
    // FIXED: Only add zoneName for actual zone names, not numeric IDs
    if (zoneId && zoneId !== 'Whole screen' && isNaN(zoneId)) {
      params.zoneName = zoneId;
    }
    return this.sendCommand('previous', params);
  }

  async pausePlayback(zoneId = null) {
    const params = {};
    // FIXED: Only add zoneName for actual zone names, not numeric IDs
    if (zoneId && zoneId !== 'Whole screen' && isNaN(zoneId)) {
      params.zoneName = zoneId;
    }
    return this.sendCommand('pause', params);
  }

  async resumePlayback(zoneId = null) {
    const params = {};
    // FIXED: Only add zoneName for actual zone names, not numeric IDs
    if (zoneId && zoneId !== 'Whole screen' && isNaN(zoneId)) {
      params.zoneName = zoneId;
    }
    return this.sendCommand('resume', params);
  }

  async playNextBackground() {
    return this.sendCommandWithFallbacks('next', [
      { panelId: 'audio' },
      { zoneName: 'audio' },
      {}
    ]);
  }

  async playPreviousBackground() {
    return this.sendCommandWithFallbacks('previous', [
      { panelId: 'audio' },
      { zoneName: 'audio' },
      {}
    ]);
  }

  async pauseBackground() {
    return this.sendCommandWithFallbacks('pause', [
      { panelId: 'audio' },
      { zoneName: 'audio' },
      {}
    ]);
  }

  async resumeBackground() {
    return this.sendCommandWithFallbacks('resume', [
      { panelId: 'audio' },
      { zoneName: 'audio' },
      {}
    ]);
  }

  // === DISPLAY CONTROL - FIXED ===
  async showHtml(html, length, zoneId = null) {
    const params = {
      html,
      length
    };
    
    // FIXED: Only add zoneName for actual zone names, not numeric IDs
    if (zoneId && zoneId !== 'Whole screen' && isNaN(zoneId)) {
      params.zoneName = zoneId;
    }
    
    return this.sendCommand('showSentHtml', params);
  }

  async showFile(filename, length, zoneId = null) {
    const params = {
      file: filename,
      length
    };
    
    // FIXED: Only add zoneName for actual zone names, not numeric IDs
    if (zoneId && zoneId !== 'Whole screen' && isNaN(zoneId)) {
      params.zoneName = zoneId;
    }
    
    return this.sendCommand('showFile', params);
  }

  // === LAYOUT CONTROL - ENHANCED ===
  async getLayouts(deviceName) {
    try {
      console.log('🖥️ Fetching layouts from device via MQTT...');
      const result = deviceName
        ? await this.sendCommandToDevice(deviceName, 'layouts')
        : await this.sendCommand('layouts');
      console.log('🖥️ Raw layouts response received:', JSON.stringify(result, null, 2));

      if (result && result.success !== false) {
        let layouts = [];

        // Check if we have result data
        if (result.result) {
          console.log('🔍 Processing result.result:', result.result);

          if (Array.isArray(result.result)) {
            layouts = result.result.map(layout => ({
              name: typeof layout === 'string' ? layout : layout.name || layout.toString(),
              description: ''
            }));
          } else if (typeof result.result === 'object' && result.result !== null) {
            layouts = Object.entries(result.result).map(([name, value]) => ({
              name: name,
              description: (typeof value === 'string' ? value : '') || ''
            }));
          }
        }

        // If we found layouts, return them
        if (layouts.length > 0) {
          console.log('✅ Found real layouts from device:', layouts);
          return { success: true, result: layouts };
        } else {
          console.warn('⚠️ No layouts found in device response');
          throw new Error('No layouts found in device response');
        }
      } else {
        throw new Error('Device returned unsuccessful response');
      }
    } catch (error) {
      console.error('❌ Error fetching layouts from device:', error);
      console.log('🔄 Device does not support layouts command - returning empty result');

      return {
        success: false,
        result: [],
        error: 'Device does not support layout management'
      };
    }
  }

  async getCurrentLayout(deviceName) {
    try {
      console.log('🖥️ Getting current layout status...');
      const deviceInfo = await this.getDeviceInfo(deviceName);
      console.log('🖥️ Layout status derived from deviceInfo:', deviceInfo);
      if (deviceInfo && deviceInfo.result && deviceInfo.result.currentLayout) {
        return {
          success: true,
          result: {
            currentLayout: deviceInfo.result.currentLayout,
            isScheduled: deviceInfo.result.isScheduledLayout || false
          }
        };
      }
      
      // If no current layout info available, return default
      return {
        success: true,
        result: {
          currentLayout: 'Scheduled',
          isScheduled: true
        }
      };
    } catch (error) {
      console.warn('⚠️ Could not get current layout:', error.message);
      return {
        success: true,
        result: {
          currentLayout: 'Unknown',
          isScheduled: true
        }
      };
    }
  }

  async setLayout(layoutName) {
    return this.sendCommand('layout/set', { layoutName });
  }

  async clearLayout() {
    return this.sendCommand('layout/clear');
  }

  // ===== Screen Sleep (black-out without powering off) =====
  // Strategy: use the agent's `showsenthtml` direct op to render full-black HTML
  // on every non-audio panel for ~24h. The device stays online — MQTT, sync,
  // brightness, etc. all keep working — but the panels show black. To wake we
  // call `playlist/clear` on the same panels which returns each zone to its
  // scheduled playlist.
  //
  // Independent of layouts/playlists: works on any device, no "sleep" layout
  // pre-creation needed. Verified live: showsenthtml requires `length` (seconds)
  // and `panelId`, plus the html body.
  _blackScreenHtml() {
    return '<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"/>'
         + '<style>html,body{margin:0;padding:0;width:100%;height:100%;background:#000;overflow:hidden;cursor:none}</style>'
         + '</head><body></body></html>';
  }

  async _mainPanelsOf(deviceName) {
    const panels = await this.listPanels(deviceName);
    // Drop audio — we only black out display panels. If panels list is empty
    // (older agent without /ajax/panels) fall back to "no panelId" which the
    // agent treats as the default zone.
    const main = (panels || []).filter(p => String(p.id).toLowerCase() !== 'audio');
    return main.length > 0 ? main : [{ id: '', name: 'default' }];
  }

  async screenSleep(deviceName) {
    const panels = await this._mainPanelsOf(deviceName);
    const html = this._blackScreenHtml();
    const length = 86400; // 24 hours — effectively "until woken"
    const results = [];
    for (const p of panels) {
      const params = { length, html };
      if (p.id) params.panelId = p.id;
      const r = deviceName
        ? await this.sendCommandToDevice(deviceName, 'showsenthtml', params)
        : await this.sendCommand('showsenthtml', params);
      results.push({ panel: p, ok: r?.success !== false });
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      try { window.localStorage.setItem(`screen_sleep_${deviceName}`, '1'); } catch (_) {}
    }
    return { success: results.every(r => r.ok), panels: results };
  }

  async screenWake(deviceName) {
    const panels = await this._mainPanelsOf(deviceName);
    const results = [];
    // CRITICAL: showsenthtml installs an InterruptableRunnable that's CURRENTLY
    // RUNNING on the panel. `playlist/clear` only resets the manually-set
    // playlist; it does NOT interrupt a running senthtml — verified by screenshot
    // diffing on a live device. Only `next` actually interrupts the runnable so
    // the panel can move on. Verified: after `next`, the screen returns to
    // scheduled content (screenshot size jumps from ~16KB black to ~220KB real).
    for (const p of panels) {
      const params = {};
      if (p.id) params.panelId = p.id;
      const r = deviceName
        ? await this.sendCommandToDevice(deviceName, 'next', params)
        : await this.sendCommand('next', params);
      results.push({ panel: p, ok: r?.success !== false });
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      try { window.localStorage.removeItem(`screen_sleep_${deviceName}`); } catch (_) {}
    }
    return { success: results.every(r => r.ok), panels: results };
  }

  isScreenAsleep(deviceName) {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    try { return window.localStorage.getItem(`screen_sleep_${deviceName}`) === '1'; }
    catch (_) { return false; }
  }
 
  // === DEVICE MANAGEMENT ===
  async reloadApp() {
    return this.sendCommand('reload');
  }

  async rebootDevice() {
    return this.sendCommand('reboot');
  }

  async playBeep() {
    return this.sendCommand('beep');
  }

  async getScreenshot(deviceName) {
    const target = deviceName || this.deviceName;
    console.log(`🖼️ Requesting screenshot via MQTT for device: ${target}`);

    let originalTimeout;
    try {
      originalTimeout = this.commandTimeout;
      // Some devices are slow to capture/encode; allow a longer timeout for screenshot.
      this.commandTimeout = Math.max(originalTimeout, 60000);
      
      const result = deviceName
        ? await this.sendCommandToDevice(deviceName, 'screenshot')
        : await this.sendCommand('screenshot');
      
      console.log(`📊 Screenshot result received:`, {
        success: result?.success,
        hasResult: !!(result?.result),
        hasData: !!(result?.result?.data),
        dataLength: result?.result?.data?.length || 0
      });
      
      return result;
    } catch (error) {
      console.error(`❌ Screenshot error:`, error.message);
      throw error;
    } finally {
      // Always restore global timeout
      // (sendCommandToDevice uses this.commandTimeout at call time)
      if (typeof originalTimeout !== 'undefined') {
        this.commandTimeout = originalTimeout;
      }
    }
  }

// === SHELL COMMAND EXECUTION ===
  async executeShell(command) {
    console.log(`🖥️ Executing shell command: ${command}`);

    await this.ensureConnection();

    const requestTopic = `${this.topicPrefix}/REQ/${this.deviceId}/SHELL`;
    const responseTopic = `${this.topicPrefix}/RESP/${this.deviceId}/SHELL`;

    return new Promise((resolve, reject) => {
      const timeoutMs = this.commandTimeout;
      const shellKey = `${this.deviceId || ''}::${command}`;

      const callback = (response) => {
        clearTimeout(timeout);
        resolve(response);
      };

      const timeout = setTimeout(() => {
        const q = this.shellCallbacks.get(shellKey);
        if (Array.isArray(q)) {
          const idx = q.indexOf(callback);
          if (idx !== -1) q.splice(idx, 1);
          if (q.length === 0) this.shellCallbacks.delete(shellKey);
          else this.shellCallbacks.set(shellKey, q);
        }
        console.log(`⏰ Shell command timeout after ${timeoutMs}ms: ${command}`);
        reject(new Error('Shell command timeout - device may be offline or slow to respond'));
      }, timeoutMs);

      const existing = this.shellCallbacks.get(shellKey);
      const queue = Array.isArray(existing) ? existing : [];
      queue.push(callback);
      this.shellCallbacks.set(shellKey, queue);

      const subscribeAndPublish = () => {
        this.client.publish(requestTopic, command, (err) => {
          if (err) {
            clearTimeout(timeout);
            const q = this.shellCallbacks.get(shellKey);
            if (Array.isArray(q)) {
              const idx = q.indexOf(callback);
              if (idx !== -1) q.splice(idx, 1);
              if (q.length === 0) this.shellCallbacks.delete(shellKey);
              else this.shellCallbacks.set(shellKey, q);
            }
            reject(new Error(`Failed to publish shell command: ${err.message}`));
          } else {
            console.log(`✅ Shell command published to ${requestTopic}`);
          }
        });
      };

      if (!this.subscribedTopics) {
        this.subscribedTopics = new Set();
      }

      if (!this.subscribedTopics.has(responseTopic)) {
        this.client.subscribe(responseTopic, (err) => {
          if (err) {
            clearTimeout(timeout);
            const q = this.shellCallbacks.get(shellKey);
            if (Array.isArray(q)) {
              const idx = q.indexOf(callback);
              if (idx !== -1) q.splice(idx, 1);
              if (q.length === 0) this.shellCallbacks.delete(shellKey);
              else this.shellCallbacks.set(shellKey, q);
            }
            reject(new Error(`Failed to subscribe to shell response topic: ${err.message}`));
            return;
          }

          this.subscribedTopics.add(responseTopic);
          console.log(`📡 Subscribed to shell responses: ${responseTopic}`);
          subscribeAndPublish();
        });
      } else {
        subscribeAndPublish();
      }
    });
  }

  // === FILE MANAGEMENT (WebDAV) ===
  async getDeviceFiles(path) {
    console.log(`📂 Requesting file list for path: ${path}`);
    
    try {
      const webdavService = (await import('./webdavService')).default;
      await webdavService.ensureAuthenticated();
      
      let webdavPath = path;
      
      if (path.startsWith('https://') || path.startsWith('http://')) {
        try {
          const url = new URL(path);
          webdavPath = url.pathname;
        } catch (urlError) {
          console.error('❌ Invalid URL format:', path);
          throw new Error(`Invalid path format: ${path}`);
        }
      }
      
      if (!webdavPath.startsWith('/')) {
        webdavPath = '/' + webdavPath;
      }

      // Normalize to a directory path.
      // Some API fields may point to a specific file (e.g. .../ph2.jpg). In that case,
      // listing must target the parent directory.
      if (!webdavPath.endsWith('/')) {
        const last = webdavPath.split('/').filter(Boolean).pop() || '';
        const looksLikeFile = last.includes('.') && !last.endsWith('.');
        if (looksLikeFile) {
          webdavPath = webdavPath.replace(/\/[^\/]+$/, '/');
          if (!webdavPath.endsWith('/')) webdavPath += '/';
        } else {
          webdavPath += '/';
        }
      }
      
      console.log(`🔍 Converted WebDAV path: ${webdavPath}`);

      // Force refresh so we don't get stale cached results when files change.
      const listing = await webdavService.getDirectoryListing(webdavPath, null, true);
      
      if (!listing || !listing.items) {
        throw new Error('Invalid response from file server');
      }
      
      const deviceFiles = listing.items
        .filter(item => item && !item.isDirectory && item.name)
        .map(file => ({
          basename: file.name,
          size: file.size || 0,
          modified: file.modified || '',
          path: file.path || `${webdavPath}${file.name}`
        }))
        .filter(file => {
          const name = file.basename.toLowerCase();
          return !name.startsWith('.') && 
                 !name.includes('thumbs.db') && 
                 !name.includes('.ds_store');
        });
      
      console.log(`✅ Found ${deviceFiles.length} files in device folder`);
      
      if (deviceFiles.length > 0) {
        console.log('📋 Sample files:', deviceFiles.slice(0, 3).map(f => f.basename));
      }
      
      return deviceFiles;
      
    } catch (error) {
      console.error(`❌ Failed to get device files from ${path}:`, error);
      
      if (error.message.includes('Authentication')) {
        throw new Error('Authentication failed. Please check WebDAV credentials.');
      } else if (error.message.includes('timeout')) {
        throw new Error('Request timed out. Server may be slow or path invalid.');
      } else if (error.message.includes('404')) {
        throw new Error('Folder not found. Check if the device folder exists.');
      } else if (error.message.includes('403')) {
        throw new Error('Access denied. Check folder permissions.');
      }
      
      throw new Error(`Failed to retrieve file list: ${error.message}`);
    }
  }

  async requestJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'include',
      ...options
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (e) {
      payload = null;
    }

    if (!response.ok) {
      const message = payload?.error || payload?.details || `Request failed (${response.status})`;
      throw new Error(message);
    }

    return payload;
  }

  async getGroups(username = null) {
    const qs = username ? `?username=${encodeURIComponent(username)}` : '';
    return this.requestJson(`/api/groups${qs}`);
  }

  async getGroupDetails(groupId) {
    return this.requestJson(`/api/groups/${groupId}`);
  }

  async createGroup(groupData) {
    return this.requestJson('/api/groups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(groupData)
    });
  }

  async updateGroup(groupId, groupData) {
    return this.requestJson(`/api/groups/${groupId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(groupData)
    });
  }

  async deleteGroup(groupId) {
    return this.requestJson(`/api/groups/${groupId}`, {
      method: 'DELETE'
    });
  }

  async getGroupFiles(groupId, folder = '') {
    const qs = folder ? `?folder=${encodeURIComponent(folder)}` : '';
    return this.requestJson(`/api/groups/${groupId}/files${qs}`);
  }

  async getGroupFolders(groupId) {
    return this.requestJson(`/api/groups/${groupId}/folders`);
  }

  uploadToGroup(groupId, files = [], targetFolder = '', onProgress = null) {
    if (!groupId) {
      return Promise.reject(new Error('groupId is required'));
    }
    if (!Array.isArray(files) || files.length === 0) {
      return Promise.reject(new Error('files are required'));
    }

    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    if (targetFolder) {
      formData.append('targetFolder', targetFolder);
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/groups/${groupId}/upload`, true);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        const percent = Math.round((event.loaded / event.total) * 100);
        if (typeof onProgress === 'function') onProgress(percent);
      };

      xhr.onload = () => {
        let payload = null;
        try {
          payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch (e) {
          payload = null;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(payload);
        } else {
          reject(new Error(payload?.error || payload?.details || `Request failed (${xhr.status})`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Upload timeout'));
      xhr.timeout = 1200000;
      xhr.send(formData);
    });
  }

  async deleteFromGroup(groupId, fileNames = []) {
    return this.requestJson(`/api/groups/${groupId}/files`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ files: fileNames })
    });
  }

  // === UTILITY METHODS ===
  async testConnection() {
    try {
      await this.ensureConnection();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  disconnect() {
    if (this.client) {
      console.log('🚪 Disconnecting MQTT client');
      this.client.end();
      this.isConnected = false;
      this.responseCallbacks.clear();
      this.pendingCommands.clear();
      if (this.subscribedTopics) {
        this.subscribedTopics.clear();
      }
    }
  }

  getConnectionStatus() {
    return {
      connected: this.isConnected,
      deviceSet: !!this.deviceName,
      pendingCommands: this.pendingCommands.size
    };
  }
}

// Create singleton instance
const promediaService = new PromediaService();

export default promediaService;